<template>
  <div class="version-selector">
    <!-- Version Section (based on mockup) -->
    <section class="version-section">
      <q-expansion-item v-model="sectionExpanded" expand-separator icon="o_history" label="Version" default-opened class="custom-expansion tool-title" @hide="handleSectionHide">
        <div class="version-content">
          <!-- Current version display -->
          <div class="current-version-wrapper">
            <div class="version-display" ref="versionDisplay">
              <div class="version-thumbnail" title="Default version in this stack">
                <img :src="getThumbnailUrl(defaultVersion || currentImage)" alt="Default version" />
              </div>
              <div class="version-display-info">
                <div class="version-info-row">
                  <div class="version-display-name">{{ currentVersionName }}</div>
                  <q-chip v-if="isCurrentVersion" dense size="sm" class="current-badge">Current</q-chip>
                  <q-tooltip> {{ currentVersionName }} </q-tooltip>
                </div>
              </div>

              <!-- Use a regular button for the dropdown toggle -->
              <q-btn flat round dense :icon="expandVersions ? 'expand_less' : 'expand_more'" class="expand-btn">
                <q-menu
                  ref="versionMenu"
                  v-model="expandVersions"
                  persistent
                  anchor="bottom left"
                  self="top left"
                  :offset="[0, 8]"
                  class="versions-menu"
                  @show="handleMenuShow"
                  @hide="handleMenuHide"
                  :style="{ minWidth: menuWidth + 'px', width: menuWidth + 'px' }"
                >
                  <div class="versions-menu-content">
                    <!-- Upload new version button -->
                    <div
                      class="upload-version-button"
                      :class="{ 'drag-active': isDragging }"
                      @click.stop="openUploadDialog"
                      @dragenter.prevent="handleDragEnter"
                      @dragover.prevent="handleDragOver"
                      @dragleave.prevent="handleDragLeave"
                      @drop.prevent="handleDrop"
                      v-close-popup.stop="false"
                    >
                      <template v-if="!isDragging">
                        Click here to create new version
                        <div class="upload-hint">Drag and drop {{ allowedContentDescription }} to upload</div>
                      </template>
                      <template v-else>
                        <div class="drag-feedback">
                          <q-icon name="cloud_upload" size="24px" />
                          <div>Drop {{ allowedContentDescription }} here to create version</div>
                        </div>
                      </template>
                    </div>

                    <!-- Versions list -->
                    <div v-if="versions.length > 0" class="versions-list">
                      <div
                        v-for="version in versions"
                        :key="version.id"
                        class="version-item"
                        :class="{
                          'is-current': isDefaultVersion(version),
                          'is-original': isOriginalVersion(version)
                        }"
                        @click="restoreVersion(version)"
                      >
                        <div class="version-item-thumbnail">
                          <img :src="getThumbnailUrl(version)" alt="Version preview" />
                        </div>
                        <div class="version-item-info">
                          <div class="version-item-name">{{ version.version_name }}</div>
                          <div class="version-badges">
                            <q-chip v-if="isDefaultVersion(version)" dense size="sm" class="current-badge">Current</q-chip>
                            <q-chip v-if="isOriginalVersion(version)" dense size="sm" class="original-badge">Original</q-chip>
                          </div>
                        </div>
                        <div class="version-item-actions">
                          <q-btn
                            flat
                            round
                            dense
                            icon="delete_outline"
                            size="sm"
                            color="negative"
                            title="Delete this version"
                            @click.stop="confirmDeleteVersion(version)"
                            v-if="versions.length > 1 && !isDefaultVersion(version) && !isOriginalVersion(version)"
                          />
                        </div>
                      </div>
                    </div>
                    <div v-else class="empty-versions">
                      <p>No version history available</p>
                    </div>
                  </div>
                </q-menu>
              </q-btn>
            </div>

            <!-- Update info -->
            <div class="version-update-info" v-if="currentImage && currentImage.id">
              <!-- Show user info and date on the same line -->
              <div v-if="currentVersionInfo && currentVersionInfo.user_name && currentVersionInfo.created_at">
                <div class="version-info-line">
                  <span class="version-name">{{ currentVersionInfo.version_name }}</span>
                  <span class="version-updated-by">by {{ currentVersionInfo.user_name }}</span>
                </div>
                <div class="version-date">
                  {{ formatDate(currentVersionInfo.created_at) }}
                  <span class="version-time-ago">{{ getTimeAgo(currentVersionInfo.created_at) }}</span>
                </div>
              </div>
              <!-- Fallback for partial info -->
              <div v-else-if="currentVersionInfo && currentVersionInfo.user_name">
                <div class="version-info-line">
                  <span class="version-name">{{ currentVersionInfo.version_name }}</span>
                  <span class="version-updated-by">by {{ currentVersionInfo.user_name }}</span>
                </div>
                <div class="version-time-ago">{{ currentImage.created_at ? getTimeAgo(currentImage.created_at) : '' }}</div>
              </div>
              <!-- Current image fallback when no version info -->
              <div v-else-if="currentImage.created_at">
                <div class="version-info-line">
                  <span class="version-name">{{ currentImage.version_name || 'V0' }}</span>
                  <span class="version-updated">updated {{ formatDate(currentImage.created_at) }}</span>
                </div>
                <div class="version-time-ago">{{ getTimeAgo(currentImage.created_at) }}</div>
              </div>
              <!-- Complete fallback -->
              <div v-else>
                <div class="version-info-line">
                  <span class="version-name">{{ currentImage.version_name || 'V0' }}</span>
                </div>
                <div class="version-time-ago">{{ currentImage.created_at ? getTimeAgo(currentImage.created_at) : '' }}</div>
              </div>
            </div>

            <!-- Restore button (only shown when not expanded and viewing a non-default version) -->
            <div v-if="!expandVersions && isCurrentImageInStack && defaultVersion && String(defaultVersion.id) !== String(currentImage.id)" class="restore-button-wrapper">
              <q-btn class="restore-button" no-caps unelevated @click="restoreCurrentImage">
                <q-icon name="restore" class="q-mr-xs" size="16px" />
                Restore to original version
              </q-btn>
            </div>
          </div>
        </div>
      </q-expansion-item>
    </section>

    <!-- End of Version Section -->

    <!-- Upload Dialog using BaseDialog component -->
    <BaseDialog
      v-model="uploadDialogOpen"
      title="Create new version"
      confirm-label="Create"
      cancel-label="Close"
      :loading="uploading"
      :disabled="!newVersionFile || !versionComment || versionComment.trim() === ''"
      @confirm="uploadNewVersion"
      @cancel="
        () => {
          resetUploadState();
          uploadDialogOpen = false;
        }
      "
      width="480px"
    >
      <p class="dialog-subtitle">Add a comment to describe the changes in this new version.</p>

      <!-- Image preview with version info -->
      <div class="version-preview-layout">
        <div class="preview-image-container">
          <img :src="newVersionPreview" alt="Upload preview" class="preview-image" />
        </div>
        <div class="version-info">
          <div class="version-label">Save as</div>
          <div class="version-name">v{{ getNextVersionNumber(versions) }}</div>
        </div>
      </div>

      <!-- Version comment section -->
      <div class="version-comment-section">
        <div class="comment-label">Version comment</div>
        <q-input
          v-model="versionComment"
          outlined
          class="comment-input"
          placeholder="Add a comment..."
          :rules="[val => (val && val.trim() !== '') || '']"
          lazy-rules
          hide-bottom-space
          autofocus
          type="textarea"
          rows="4"
        />
      </div>
    </BaseDialog>

    <!-- Hidden file input for file selection -->
    <input ref="fileInput" type="file" :accept="acceptedFileTypes" style="display: none" @change="handleFileSelected" />
  </div>
</template>

<script>
import { ref, computed, watch, watchEffect, nextTick, onMounted } from '@vue/composition-api';
import { ContentApi } from '@/api/modules/content';
import fileUpload from '@/utils/fileUpload';
import helpers from '@/utils/globalMixins';
import BaseDialog from '@/components/base/BaseDialog.vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { debounce } from 'lodash';

export default {
  name: 'VersionSelector',

  components: {
    BaseDialog
  },

  mixins: [helpers],

  props: {
    currentImage: {
      type: Object,
      required: true
    }
  },

  setup(props, { emit, root }) {
    // Data
    const expandVersions = ref(false);
    const sectionExpanded = ref(true); // Track if the section is expanded
    const uploadDialogOpen = ref(false);
    const fileInput = ref(null);
    const newVersionFile = ref(null);
    const newVersionPreview = ref(null);
    const versionComment = ref('');
    const uploading = ref(false);
    const versionDisplay = ref(null); // Ref for the version display area
    const menuWidth = ref(0); // Ref for menu width, initialized to 0
    const isDragging = ref(false); // Add drag state for visual feedback

    // New state variables for coordinated loading
    const versionSwitchingState = ref({
      isLoading: false,
      currentOperation: null, // 'selecting', 'restoring', 'uploading'
      targetVersionId: null
    });
    const lastSelectedVersion = ref(null);
    const versionChangeMutex = ref(false); // Prevent concurrent operations

    // Set up query client
    const queryClient = useQueryClient();

    // Update menu width when window resizes
    const handleResize = () => {
      if (versionDisplay.value) {
        // Get the exact width of the parent element for perfect alignment
        menuWidth.value = versionDisplay.value.offsetWidth;
      }
    };

    // Calculate menu width based on parent element and set up resize listener
    onMounted(() => {
      nextTick(() => {
        handleResize();
      });
      window.addEventListener('resize', handleResize);
    });

    // Helper function to check if a version is the default version
    function isDefaultVersion(version) {
      return version.is_default === true || version.is_default === 'true' || String(version.is_default).toLowerCase() === 'true';
    }

    // Preload version image to prevent loading delays
    const preloadVersionImage = async version => {
      if (!version?.url) return Promise.resolve();

      return new Promise((resolve, reject) => {
        const img = new Image();
        const timeout = setTimeout(() => {
          reject(new Error('Image preload timeout'));
        }, 5000); // 5 second timeout

        img.onload = () => {
          clearTimeout(timeout);
          resolve();
        };

        img.onerror = () => {
          clearTimeout(timeout);
          // Don't reject on image error, just resolve to continue
          resolve();
        };

        img.src = version.url;
      });
    };

    // Preload version thumbnails for better UX
    const preloadVersionThumbnails = async versions => {
      if (!Array.isArray(versions)) return;

      const preloadPromises = versions.slice(0, 5).map(version => {
        return preloadVersionImage(version).catch(() => {
          // Silently handle preload failures
        });
      });

      try {
        await Promise.allSettled(preloadPromises);
      } catch (error) {
        // Silently handle batch preload failures
      }
    };

    // Helper function to check if a version is the original version (V0)
    function isOriginalVersion(version) {
      return (
        version.is_original === true ||
        version.is_original === 'true' ||
        String(version.is_original).toLowerCase() === 'true' ||
        (version.version_name && (version.version_name === 'V0' || version.version_name.toLowerCase() === 'v0'))
      );
    }

    // Helper function to extract version number from version name
    function extractVersionNumber(versionName) {
      if (!versionName) return -1; // Return -1 for invalid/missing version names
      // Match numbers with optional 'v' or 'V' prefix
      const match = String(versionName).match(/^v?(\d+)$/i);
      return match ? parseInt(match[1], 10) : -1; // Return -1 for invalid format
    }

    function addHttpsIfNoExisted(url) {
      // Return empty string if url is not provided
      if (!url || typeof url !== 'string') {
        return '';
      }

      // Prepend protocol only when missing
      if (!url.startsWith('https://') && !url.startsWith('http://')) {
        return `https://${url}`;
      }
      return url;
    }

    // Computed
    const isCurrentVersion = computed(() => {
      // Always return true since we're always showing the default version info
      return true;
    });

    // Get the default version from the stack
    const defaultVersion = computed(() => {
      if (!Array.isArray(versions.value) || versions.value.length === 0) return null;

      // Find the default version in the stack
      const defaultVer = versions.value.find(v => isDefaultVersion(v));

      // If no default version found, use the first version in the stack
      const result = defaultVer || versions.value[0];

      // Emit the default version ID to the parent for comment fetching
      if (result && result.id) {
        emit('default-version-id', result.id);
      }

      return result;
    });

    // URL to use for the version thumbnail (current image being viewed)
    // Helper function to get thumbnail URL for any content (image or video)
    const getThumbnailUrl = content => {
      if (!content) return '';

      // For videos, prefer preview_url or placeholder_url
      if (content.type === 'video') {
        return addHttpsIfNoExisted(content.placeholder_url);
      }

      // For images, use the regular url
      return content.url;
    };

    // Get info about the default version in the stack
    const currentVersionInfo = computed(() => {
      if (!Array.isArray(versions.value) || versions.value.length === 0) return null;

      // Use the default version computed property
      const versionInfo = defaultVersion.value;

      // Ensure we have at least minimal info if a version was found
      if (versionInfo) {
        // Get user info from the current user if available
        const currentUser = root.$store.getters['user/userData'] || {};

        // Determine the most appropriate user name
        let userName = null;

        // First try uploaded_by from the current image
        if (props.currentImage && props.currentImage.uploaded_by) {
          userName = props.currentImage.uploaded_by;
        }

        // If not found, try uploaded_by from version info
        if (!userName && versionInfo.uploaded_by && versionInfo.uploaded_by !== 'Unknown') {
          userName = versionInfo.uploaded_by;
        }

        // Then try user_name
        if (!userName && versionInfo.user_name && versionInfo.user_name !== 'Unknown') {
          userName = versionInfo.user_name;
        }

        // If we still don't have a user name, get from store
        if (!userName && versionInfo.user_id) {
          // If we have a user_id, try to match with current user
          if (currentUser.id === versionInfo.user_id) {
            userName = currentUser.name || currentUser.email || null;
          }
          // Could potentially fetch user info by ID from API here
        }

        // Final fallback
        if (!userName) {
          userName = currentUser.name || currentUser.email || 'You';
        }

        // Add default values for missing fields
        const result = {
          ...versionInfo,
          user_name: userName,
          created_at: versionInfo.created_at || new Date().toISOString(),
          version_name: versionInfo.version_name || 'V0'
        };

        return result;
      }

      return null;
    });

    const currentVersionName = computed(() => {
      if (!Array.isArray(versions.value) || versions.value.length === 0) return 'V0';

      // Use the default version computed property
      const versionInfo = defaultVersion.value;
      if (!versionInfo) return 'V0';

      // Use the version name if available and valid
      if (versionInfo.version_name && typeof versionInfo.version_name === 'string' && !versionInfo.version_name.includes('NaN')) {
        return versionInfo.version_name;
      }

      // Fallback to V1 if no valid version name found
      return 'V1';
    });

    // Get the next version number based on existing versions
    const getNextVersionNumber = versions => {
      if (!Array.isArray(versions) || versions.length === 0) return 1;

      // Extract all version numbers, filter out invalid ones, and sort
      const versionNumbers = versions
        .map(version => extractVersionNumber(version.version_name))
        .filter(num => num >= 0) // Include V0 and above
        .sort((a, b) => b - a); // Sort descending to get max first

      if (versionNumbers.length === 0) return 1;

      // Return the next version number (max + 1)
      const maxVersion = versionNumbers[0];
      return maxVersion + 1;
    };

    // Check if the current image is part of the version stack
    const isCurrentImageInStack = computed(() => {
      if (!props.currentImage || !props.currentImage.id || !Array.isArray(versions.value)) {
        return false;
      }

      // Convert to string for consistent comparison
      const currentImageIdStr = String(props.currentImage.id);

      // Check if the image exists in the versions array
      return versions.value.some(version => String(version.id) === currentImageIdStr);
    });

    // Methods
    // Mutation for restoring a version
    const restoreVersionMutation = useMutation({
      mutationFn: versionId =>
        ContentApi.restoreContentVersion({
          type: props.currentImage?.type,
          id: versionId
        }),
      onMutate: async versionId => {
        const stackId = props.currentImage?.stack_id;
        if (!stackId) return;

        // Cancel any outgoing refetches
        await queryClient.cancelQueries({ queryKey: ['versions', stackId] });

        // Snapshot the previous value
        const previousVersions = queryClient.getQueryData(['versions', stackId]);

        if (previousVersions && Array.isArray(previousVersions)) {
          // Optimistically update the default version
          const optimisticVersions = previousVersions.map(version => ({
            ...version,
            is_default: version.id === versionId || String(version.id) === String(versionId)
          }));

          queryClient.setQueryData(['versions', stackId], optimisticVersions);
        }

        return { previousVersions, stackId };
      },
      onError: (err, versionId, context) => {
        // Rollback on error
        if (context?.previousVersions && context?.stackId) {
          queryClient.setQueryData(['versions', context.stackId], context.previousVersions);
        }

        // Reset loading state on error
        versionSwitchingState.value = {
          isLoading: false,
          currentOperation: null,
          targetVersionId: null
        };
        versionChangeMutex.value = false;

        // Show error notification
        root.$q.notify({
          message: 'Failed to restore version',
          color: 'negative',
          icon: 'error',
          position: 'top',
          timeout: 3000
        });
      },
      onSuccess: (response, versionId, context) => {
        const stackId = context?.stackId || props.currentImage?.stack_id;

        // Wait a moment then invalidate to get real data
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['versions', stackId] });
        }, 100);

        // Convert versionId to string for consistent comparison
        const versionIdStr = String(versionId);

        // Find the version that was restored to get its metadata - using string comparison
        const restoredVersion = versions.value.find(v => String(v.id) === versionIdStr);

        // Create a complete version object with all necessary data
        const completeVersionData = {
          imageId: versionId,
          versionName: restoredVersion?.version_name || '',
          originalImageId: props.currentImage?.id,
          versionData: {
            ...restoredVersion,
            // Ensure we don't modify the original URL in any way
            url: restoredVersion?.url || ''
          }
        };

        // Reset loading state
        versionSwitchingState.value = {
          isLoading: false,
          currentOperation: null,
          targetVersionId: null
        };
        versionChangeMutex.value = false;

        // Show success notification
        root.$q.notify({
          message: 'Version restored successfully',
          color: 'positive',
          icon: 'check_circle',
          position: 'top',
          timeout: 2000
        });

        // Emit event to parent with complete version information
        emit('version-restored', completeVersionData);
      }
    });

    // Debounced version of restore operation to prevent rapid clicking
    const debouncedRestoreVersion = debounce(async version => {
      if (!version || !version.id || versionChangeMutex.value) return;

      // Acquire mutex to prevent concurrent operations
      versionChangeMutex.value = true;

      try {
        // Set loading state
        versionSwitchingState.value = {
          isLoading: true,
          currentOperation: 'restoring',
          targetVersionId: version.id
        };

        // Store the version for reference
        lastSelectedVersion.value = version;

        // Preload the version image before UI update
        await preloadVersionImage(version);

        // Emit version-selected with loading state information
        emit('version-selected', {
          ...version,
          _loadingState: {
            isPreloaded: true,
            operation: 'restoring'
          }
        });

        // Then restore the version as default
        restoreVersionMutation.mutate(version.id);
      } catch (error) {
        console.error('Error in version restoration:', error);
        // Reset state on error
        versionSwitchingState.value = {
          isLoading: false,
          currentOperation: null,
          targetVersionId: null
        };
        versionChangeMutex.value = false;
      }
    }, 300);

    const restoreVersion = version => {
      debouncedRestoreVersion(version);
    };

    // Use TanStack Query to fetch and manage versions data
    const versionsQuery = useQuery({
      queryKey: ['versions', computed(() => props.currentImage?.stack_id)],
      queryFn: async () => {
        if (!props.currentImage || !props.currentImage.stack_id) {
          return [];
        }

        try {
          // Use the unified API endpoint with content type
          const response = await ContentApi.getContentByStackId({
            stackId: props.currentImage.stack_id,
            contentType: props.currentImage.type
          });

          if (response.data && response.data.content && Array.isArray(response.data.content)) {
            // Sort versions by creation date to ensure consistent ordering
            const sortedContent = response.data.content.sort((a, b) => {
              const dateA = new Date(a.created_at || 0);
              const dateB = new Date(b.created_at || 0);
              return dateA - dateB; // Oldest first
            });

            // Process the versions array to ensure consistent properties
            const processedVersions = sortedContent.map(img => {
              // Normalize is_default to boolean - handle string 'true'/'false' values properly
              const isDefault = isDefaultVersion(img);

              // Preserve original version_name - only fix if it's completely missing or invalid
              let versionName = img.version_name;
              if (!versionName || typeof versionName !== 'string' || versionName.trim() === '') {
                // Only assign fallback if no version name exists
                // Use V0 for original/default versions, otherwise calculate next number
                if (isDefault || img.is_original) {
                  versionName = 'V0';
                } else {
                  // Find the highest existing version number and add 1
                  const existingNumbers = sortedContent
                    .map(v => extractVersionNumber(v.version_name))
                    .filter(num => num >= 0)
                    .sort((a, b) => b - a); // Sort descending to get max first

                  const maxVersion = existingNumbers.length > 0 ? existingNumbers[0] : 0;
                  versionName = `V${maxVersion + 1}`;
                }
              }

              const processedVersion = {
                ...img,
                // Ensure ID is the same type (number) for consistent comparison
                id: parseInt(img.id, 10) || img.id,
                is_default: isDefault,
                version_name: versionName,
                // Ensure these fields exist with fallbacks
                user_name: img.user_name || 'Unknown',
                created_at: img.created_at || new Date().toISOString()
              };

              return processedVersion;
            });

            return processedVersions;
          }

          return [];
        } catch (error) {
          return [];
        }
      },
      enabled: computed(() => !!props.currentImage?.stack_id),
      staleTime: 1000 * 60, // 1 minute before refetching
      refetchOnWindowFocus: false,
      keepPreviousData: true, // Keep previous data while refetching to prevent empty flash
      placeholderData: [] // Provide empty array as placeholder to prevent undefined
    });

    // Computed property for versions from query result
    const versions = computed(() => {
      const data = versionsQuery.data.value || [];
      return data;
    });

    // Refresh versions when needed
    const refreshVersions = async () => {
      try {
        if (!props.currentImage?.stack_id) {
          return;
        }

        // Force a refetch of version data
        await versionsQuery.refetch();
      } catch (error) {
        // Handle errors silently
      }
    };

    const handleFileSelected = event => {
      const file = event.target.files[0];
      if (file) {
        handleNewVersionFile(file);
      }
    };

    // Computed property for accepted file types based on current content type
    const acceptedFileTypes = computed(() => {
      if (!props.currentImage) return '';

      // If current content is a video, only allow video uploads
      if (props.currentImage.type === 'video') {
        return 'video/mp4,video/quicktime';
      }

      // If current content is an image, only allow image uploads
      return 'image/jpeg,image/jpg,image/png';
    });

    // Computed property for allowed content type description
    const allowedContentDescription = computed(() => {
      if (!props.currentImage) return 'files';

      if (props.currentImage.type === 'video') {
        return 'videos';
      }

      return 'images';
    });

    // Computed property for allowed file types array
    const allowedFileTypes = computed(() => {
      if (!props.currentImage) return [];

      if (props.currentImage.type === 'video') {
        return ['video/mp4', 'video/quicktime'];
      }

      return ['image/jpeg', 'image/jpg', 'image/png'];
    });

    // Computed property for error message content type description
    const errorContentDescription = computed(() => {
      if (!props.currentImage) return 'supported files';

      if (props.currentImage.type === 'video') {
        return 'videos (MP4, MOV)';
      }

      return 'images (JPEG, JPG, PNG)';
    });

    const handleDrop = event => {
      isDragging.value = false; // Reset drag state
      const file = event.dataTransfer.files[0];
      if (file) {
        // Validate file type based on current content type
        if (!isValidFileType(file)) {
          root.$q.notify({
            message: `Please drop ${errorContentDescription.value} only`,
            color: 'negative',
            position: 'top',
            timeout: 2000
          });
          return;
        }

        // Check file size (optional - 10MB limit)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          root.$q.notify({
            message: 'File size too large. Please select a file smaller than 10MB.',
            color: 'negative',
            position: 'top',
            timeout: 2000
          });
          return;
        }

        // Show success notification for valid file
        root.$q.notify({
          message: `${file.name} ready for version upload`,
          color: 'positive',
          position: 'top',
          timeout: 2000
        });

        handleNewVersionFile(file);
      }
    };

    const handleDragEnter = event => {
      event.preventDefault();
      isDragging.value = true;
    };

    const handleDragOver = event => {
      event.preventDefault();
      isDragging.value = true;
    };

    const handleDragLeave = event => {
      event.preventDefault();
      // Only reset if we're actually leaving the drop zone
      if (!event.currentTarget.contains(event.relatedTarget)) {
        isDragging.value = false;
      }
    };

    const triggerFileInput = () => {
      fileInput.value.click();
    };

    const handleNewVersionFile = file => {
      if (!file) return;

      // Validate file type based on current content type
      if (!isValidFileType(file)) {
        root.$q.notify({
          message: `Please select ${errorContentDescription.value} only`,
          color: 'negative',
          position: 'top',
          timeout: 2000
        });
        return;
      }

      // Set the new version file
      newVersionFile.value = file;
      versionComment.value = ''; // Reset comment when new file is selected

      // Check if file is a video
      const isVideo = file.type.startsWith('video/');

      if (isVideo) {
        // Create a video element to extract thumbnail
        const video = document.createElement('video');
        video.preload = 'metadata';

        // Create object URL for the video file
        const objectURL = URL.createObjectURL(file);
        video.src = objectURL;

        video.onloadedmetadata = () => {
          // Seek to a frame (either 1 second in or 25% of duration)
          video.currentTime = Math.min(1, video.duration * 0.25);

          video.onseeked = () => {
            // Create canvas to capture the frame
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            // Draw the video frame to the canvas
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            // Get the data URL from the canvas
            newVersionPreview.value = canvas.toDataURL('image/jpeg');

            // Open the dialog after we have the preview
            uploadDialogOpen.value = true;

            // Clean up the object URL
            URL.revokeObjectURL(objectURL);
          };
        };

        video.onerror = () => {
          console.error('Error loading video for thumbnail extraction');
          // Fall back to a generic video icon or placeholder
          uploadDialogOpen.value = true;
          URL.revokeObjectURL(objectURL);
        };
      } else {
        // Original method for image files
        const reader = new FileReader();
        reader.onload = e => {
          newVersionPreview.value = e.target.result;
          // Open the dialog after we have the preview
          uploadDialogOpen.value = true;
        };
        reader.readAsDataURL(file);
      }
    };

    const resetUploadState = () => {
      newVersionFile.value = null;
      newVersionPreview.value = null;
      versionComment.value = '';

      // Reset the file input value to allow selecting the same file again
      if (fileInput.value) {
        fileInput.value.value = '';
      }
    };

    const openUploadDialog = () => {
      // Only trigger file input if we don't have a file selected yet
      if (!newVersionFile.value && !newVersionPreview.value) {
        triggerFileInput();
      } else {
        // If we already have a file, just open the dialog
        uploadDialogOpen.value = true;
      }
    };

    // Calculate time ago in a human-readable format
    const getTimeAgo = timestamp => {
      if (!timestamp) return 'unknown time';

      const now = new Date();
      const date = new Date(timestamp);
      const diffMs = now - date;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHours = Math.floor(diffMin / 60);
      const diffDays = Math.floor(diffHours / 24);
      const diffMonths = Math.floor(diffDays / 30);

      if (diffMonths > 0) {
        return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
      } else if (diffDays > 0) {
        return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
      } else if (diffHours > 0) {
        return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
      } else if (diffMin > 0) {
        return diffMin === 1 ? '1 minute ago' : `${diffMin} minutes ago`;
      } else {
        return 'just now';
      }
    };

    // Format file size to human-readable format
    const formatFileSize = bytes => {
      if (!bytes) return '0 Bytes';

      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Mutation for creating a stack
    const createStackMutation = useMutation({
      mutationFn: description => ContentApi.createStack({ description })
    });

    // Mutation for adding content to a stack
    const addContentToStackMutation = useMutation({
      mutationFn: params =>
        ContentApi.addContentToStack({
          type: params.type,
          id: params.id,
          stackId: params.stackId,
          versionName: params.versionName,
          versionComment: params.versionComment,
          versionContentId: params.versionContentId
        }),
      onMutate: async params => {
        // Cancel any outgoing refetches to avoid overwriting our optimistic update
        await queryClient.cancelQueries({ queryKey: ['versions', params.stackId] });

        // Snapshot the previous value
        const previousVersions = queryClient.getQueryData(['versions', params.stackId]);

        // Optimistically add the new version to the list
        if (previousVersions && Array.isArray(previousVersions)) {
          const optimisticVersion = {
            id: params.versionContentId,
            version_name: params.versionName,
            is_default: false,
            user_name: 'You',
            created_at: new Date().toISOString(),
            // Copy other properties from current image for consistency
            type: params.type,
            stack_id: params.stackId
          };

          // Add the optimistic version to the end of the list
          const updatedVersions = [...previousVersions, optimisticVersion];
          queryClient.setQueryData(['versions', params.stackId], updatedVersions);
        }

        // Return context for rollback
        return { previousVersions };
      },
      onError: (err, params, context) => {
        // If the mutation fails, rollback to the previous value
        if (context?.previousVersions) {
          queryClient.setQueryData(['versions', params.stackId], context.previousVersions);
        }

        // Show error notification
        root.$q.notify({
          message: 'Failed to add version',
          color: 'negative',
          position: 'top',
          timeout: 2000
        });
      },
      onSuccess: async (_, params) => {
        // Wait for a moment to ensure backend processing is complete
        await new Promise(resolve => setTimeout(resolve, 100));

        // Invalidate and refetch versions query to get the real data
        await queryClient.invalidateQueries({ queryKey: ['versions', params.stackId] });

        // Wait for the versions to refresh and then select the new version
        await nextTick();

        // Wait a bit more for the query to complete
        setTimeout(async () => {
          // Refetch to ensure we have the latest data
          await versionsQuery.refetch();

          // Find the newly uploaded version by matching the versionContentId
          const updatedVersions = versions.value || [];
          const newVersion = updatedVersions.find(v => v.id === params.versionContentId);

          if (newVersion) {
            // Emit version-selected event to update the main preview
            emit('version-selected', newVersion);
          }
        }, 150); // Slightly longer delay for better UX

        // Emit event to parent for UI refresh
        emit('version-added', { imageId: params.id, stackId: params.stackId });

        // Show success notification
        root.$q.notify({
          message: 'New version added successfully',
          color: 'positive',
          position: 'top',
          timeout: 2000
        });

        // Reset upload state
        uploadDialogOpen.value = false;
        newVersionFile.value = null;
        newVersionPreview.value = null;
        versionComment.value = '';
      },
      onSettled: () => {
        // Always reset upload state
        uploading.value = false;
      }
    });

    const uploadNewVersion = async () => {
      if (!newVersionFile.value || !props.currentImage) return;

      uploading.value = true;
      let globalUpload = null; // Declare in proper scope

      try {
        // Close the upload dialog first
        uploadDialogOpen.value = false;

        // Add upload to GlobalUploadProgress tracking
        const globalUploads = await root.$store.dispatch('uploadProgress/addUploads', [newVersionFile.value]);
        globalUpload = globalUploads[0]; // Get the first (and only) upload

        // Show GlobalUploadProgress dialog
        root.$store.dispatch('uploadProgress/showDialog');

        // Update progress for preparation phase - start low
        await root.$store.dispatch('uploadProgress/updateUpload', {
          id: globalUpload.id,
          data: {
            status: 'uploading'
          }
        });

        // Upload file using fileUpload utility - NO progress callback to avoid conflicts
        const newImage = await fileUpload.uploadFile(newVersionFile.value);

        if (!newImage || !newImage.id) {
          throw new Error('Invalid response from upload');
        }

        const newImageId = newImage.id;
        let stackId = props.currentImage.stack_id;
        let isNewStack = false;

        // Create stack if needed
        if (!stackId) {
          isNewStack = true;
          const stackResult = await createStackMutation.mutateAsync(`Version stack for ${props.currentImage.filename}`);
          stackId = stackResult.data.stack.id;
        }

        // Add version to stack using mutation
        const currentVersions = versions.value || [];
        const nextVersionNumber = getNextVersionNumber(currentVersions);
        const versionName = `V${nextVersionNumber}`;

        await addContentToStackMutation.mutateAsync({
          id: props.currentImage.id,
          type: props.currentImage.type,
          stackId: stackId,
          versionName: versionName,
          versionComment: versionComment.value,
          versionContentId: newImageId
        });

        // Update global progress with real content ID - let API handle progress from here
        await root.$store.dispatch('uploadProgress/updateUploadWithRealId', {
          tempId: globalUpload.id,
          realId: newImageId,
          data: {
            canDelete: true,
            contentType: props.currentImage.type // Explicitly set - versions are always images
          }
        });

        // Show success notification
        root.$q.notify({
          message: 'New version added successfully',
          color: 'positive',
          position: 'top',
          timeout: 2000
        });

        // Invalidate content queries to refresh gallery views
        queryClient.invalidateQueries({ queryKey: ['content'] });

        // Reset upload state
        newVersionFile.value = null;
        newVersionPreview.value = null;
        versionComment.value = '';

        // Special handling for legacy images - force reload content
        if (isNewStack) {
          // Force refresh the versions list
          await refreshVersions();

          // Emit an event so parent components know to refresh data
          emit('version-added', {
            imageId: props.currentImage.id,
            stackId: stackId,
            isNewStack: true
          });
        }
      } catch (error) {
        // Update global progress with error state if we have a reference
        if (globalUpload) {
          await root.$store.dispatch('uploadProgress/updateUpload', {
            id: globalUpload.id,
            data: {
              progress: 100,
              status: 'error',
              message: error.message || 'Failed to create version'
            }
          });
        }

        // Error handling
        root.$q.notify({
          message: 'Failed to upload new version',
          color: 'negative',
          position: 'top',
          timeout: 2000
        });

        console.error('Error uploading version:', error);
      } finally {
        uploading.value = false;
      }
    };

    // Watches for dependencies that should trigger refetches
    watch(
      () => props.currentImage?.id,
      () => {
        // When current image changes, ensure versions are up-to-date
        if (props.currentImage?.stack_id) {
          refreshVersions();
        }
      }
    );

    watch(
      () => props.currentImage?.stack_id,
      newStackId => {
        if (newStackId) {
          refreshVersions();
        }
      }
    );

    // Use a watch effect to automatically refresh versions when needed
    watchEffect(() => {
      props.currentImage?.stack_id;
    });

    // Debounced version selection to prevent rapid clicking
    const debouncedSelectVersion = debounce(async version => {
      if (!version || !version.id || versionChangeMutex.value) return;

      // Acquire mutex to prevent concurrent operations
      versionChangeMutex.value = true;

      try {
        // Set loading state
        versionSwitchingState.value = {
          isLoading: true,
          currentOperation: 'selecting',
          targetVersionId: version.id
        };

        // Store the version for reference
        lastSelectedVersion.value = version;

        // Preload the version image before UI update
        await preloadVersionImage(version);

        // Emit the version-selected event with loading state information
        emit('version-selected', {
          ...version,
          _loadingState: {
            isPreloaded: true,
            operation: 'selecting'
          }
        });

        // Reset loading state after a short delay
        setTimeout(() => {
          versionSwitchingState.value = {
            isLoading: false,
            currentOperation: null,
            targetVersionId: null
          };
          versionChangeMutex.value = false;
        }, 500);
      } catch (error) {
        console.error('Error in version selection:', error);
        // Reset state on error
        versionSwitchingState.value = {
          isLoading: false,
          currentOperation: null,
          targetVersionId: null
        };
        versionChangeMutex.value = false;
      }
    }, 200);

    const selectVersion = version => {
      debouncedSelectVersion(version);
    };

    // Method to restore current image when viewing a non-default version
    const restoreCurrentImage = () => {
      if (!props.currentImage || !props.currentImage.id) return;

      // Call the restore API with the current image ID
      restoreVersionMutation.mutate(props.currentImage.id);
    };

    // Methods for version deletion
    const confirmDeleteVersion = version => {
      // Use the global delete confirmation store with version type
      root.$store.dispatch('deleteConfirmation/showDeleteConfirmation', {
        item: version,
        type: 'version', // Specify this is a version deletion
        contentCount: 1,
        contentType: 'version',
        onConfirm: async deletedVersion => {
          try {
            // Post-deletion actions (refresh, notifications, etc.)
            await handleVersionDeleted(deletedVersion);
          } catch (error) {
            console.error('Error handling version deletion:', error);
            throw error; // Re-throw to be handled by the global system
          }
        }
      });
    };

    const handleVersionDeleted = async version => {
      if (!version || !version.id) return;

      try {
        // Show success notification
        root.$q.notify({
          message: `Version ${version.version_name} deleted successfully`,
          color: 'positive',
          position: 'top',
          timeout: 2000
        });

        // Force a complete refresh of the versions data
        await refreshVersions();

        // Invalidate content queries to refresh ImagesGrid
        queryClient.invalidateQueries({ queryKey: ['content'] });

        // If we deleted the current version, find and set a new default
        if (props.currentImage.id === version.id) {
          // Wait for the versions to be updated
          await nextTick();

          // Get the updated versions
          const updatedVersions = versions.value || [];
          if (updatedVersions.length > 0) {
            // Find the new default version
            const defaultVer = updatedVersions.find(v => isDefaultVersion(v)) || updatedVersions[0];
            if (defaultVer) {
              emit('version-changed', defaultVer);
            }
          }
        }
      } catch (error) {
        console.error('Error handling version deletion:', error);
        // Show error notification
        root.$q.notify({
          message: 'Error updating version list',
          color: 'negative',
          position: 'top',
          timeout: 2000
        });
        throw error; // Re-throw for global error handling
      }
    };

    // Methods to handle menu show/hide
    const handleMenuShow = () => {
      expandVersions.value = true;
      nextTick(() => {
        handleResize();

        // Auto-scroll to default version if it's the last item
        if (versions.value && versions.value.length > 5) {
          const lastVersion = versions.value[versions.value.length - 1];
          const hasDefaultAsLastItem = lastVersion && isDefaultVersion(lastVersion);

          if (hasDefaultAsLastItem) {
            // Find the versions list container and scroll to bottom
            const versionsListEl = document.querySelector('.versions-list');
            if (versionsListEl) {
              // Small delay to ensure DOM is fully rendered
              setTimeout(() => {
                versionsListEl.scrollTop = versionsListEl.scrollHeight;
              }, 50);
            }
          }
        }
      });
    };

    const handleMenuHide = () => {
      expandVersions.value = false;
    };

    // Watch the expansion state with immediate and deep options
    // Handle section hide event to close version dropdown
    const handleSectionHide = () => {
      // Close the version dropdown when section is being hidden/collapsed
      if (expandVersions.value) {
        expandVersions.value = false;
      }
    };

    // Watch for versions array changes to auto-scroll to default if it's last
    watch(
      () => versions.value,
      newVersions => {
        // Only auto-scroll if menu is currently open and we have enough versions
        if (expandVersions.value && newVersions && newVersions.length > 5) {
          const lastVersion = newVersions[newVersions.length - 1];
          const hasDefaultAsLastItem = lastVersion && isDefaultVersion(lastVersion);

          if (hasDefaultAsLastItem) {
            // Find the versions list container and scroll to bottom
            nextTick(() => {
              const versionsListEl = document.querySelector('.versions-list');
              if (versionsListEl) {
                // Small delay to ensure DOM is fully updated
                setTimeout(() => {
                  versionsListEl.scrollTop = versionsListEl.scrollHeight;
                }, 100);
              }
            });
          }
        }

        // Preload version thumbnails when versions are loaded
        if (newVersions && newVersions.length > 0) {
          nextTick(() => {
            preloadVersionThumbnails(newVersions);
          });
        }
      },
      { deep: true }
    );

    // Helper function to validate file type against current content type
    const isValidFileType = file => {
      if (!props.currentImage || !file) return false;

      const allowedTypes = allowedFileTypes.value;
      return allowedTypes.includes(file.type);
    };

    return {
      // Data
      expandVersions,
      sectionExpanded,
      uploadDialogOpen,
      newVersionFile,
      newVersionPreview,
      versionComment,
      uploading,
      fileInput,
      versionDisplay,
      menuWidth,
      isDragging,
      versionSwitchingState,
      lastSelectedVersion,

      // Computed
      versions,
      isCurrentVersion,
      currentVersionName,
      currentVersionInfo,
      versionsLoading: computed(() => versionsQuery.isLoading.value),
      isCurrentImageInStack,
      defaultVersion,
      isOriginalVersion,
      acceptedFileTypes,
      allowedContentDescription,
      errorContentDescription,
      allowedFileTypes,

      // Methods
      openUploadDialog,
      handleFileSelected,
      handleDrop,
      uploadNewVersion,
      restoreVersion,
      restoreCurrentImage,
      refreshVersions,
      formatFileSize,
      getTimeAgo,
      confirmDeleteVersion,
      triggerFileInput,
      handleNewVersionFile,
      isDefaultVersion,
      selectVersion,
      handleMenuShow,
      handleMenuHide,
      handleSectionHide,
      handleVersionDeleted,
      getNextVersionNumber,
      resetUploadState,
      handleDragEnter,
      handleDragOver,
      handleDragLeave,
      getThumbnailUrl,
      isValidFileType
    };
  }
};
</script>

<style lang="scss" scoped>
@import './version-selector.scss';
</style>
